/** @file CUBE_FACES: the orientation cube's six faces (axis label, world normal, colour, what a click does). */
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { VIEWPORT_COLORS } from './viewportColors';

/** Face ids by Pose3D's document axes (z up, y north, x east), the names the inspector and every agent use. */
export type CubeFaceId = '+X' | '-X' | '+Y' | '-Y' | '+Z' | '-Z';

/** A side face snaps the camera yaw (its world bearing, clockwise from north); up / down snap the pitch. */
export type CubeFaceKind = { kind: 'side'; bearingDeg: number } | { kind: 'up' } | { kind: 'down' };

/** One face: id, the word under it, hover title, outward normal in pose world (z up), label colour. */
export type CubeFace = CubeFaceKind & { id: CubeFaceId; word: string; title: string; normal: Vec3; color: string };

const C = VIEWPORT_COLORS;

/**
 * The faces named by the pose axes (TK ruling 2026-10-21: the user-facing truth is the document's z-up frame; the
 * Babylon scene stays y-up internally): X red, Y green, Z blue, both signs in the axis colour.
 */
export const CUBE_FACES: readonly CubeFace[] = [
  { id: '+X', word: 'east', title: '+X east: view from the east', normal: [1, 0, 0], color: C.axisX, kind: 'side', bearingDeg: 90 },
  { id: '-X', word: 'west', title: '-X west: view from the west', normal: [-1, 0, 0], color: C.axisX, kind: 'side', bearingDeg: 270 },
  { id: '+Y', word: 'north', title: '+Y north: view from the north', normal: [0, 1, 0], color: C.axisY, kind: 'side', bearingDeg: 0 },
  { id: '-Y', word: 'south', title: '-Y south: view from the south', normal: [0, -1, 0], color: C.axisY, kind: 'side', bearingDeg: 180 },
  { id: '+Z', word: 'up', title: '+Z up: steepest pitch the profile allows', normal: [0, 0, 1], color: C.axisZ, kind: 'up' },
  { id: '-Z', word: 'down', title: '-Z down: lowest pitch the profile allows', normal: [0, 0, -1], color: C.axisZ, kind: 'down' },
];
